import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { type CanvasShape, LINE_SHAPE_KINDS } from "@/store/useCanvasStore";
import { CV } from "@/config/canvasSizing";
import { THEME, colorIconClass } from "@/config/theme";
import { parseDrawing, strokePath } from "@/utils/drawing";

// Renders a freehand stroke. Points in content are relative to the shape bbox
// origin, so they map directly onto a w-full/h-full overflow-visible svg.
function DrawingBody({ content }: { content?: string }) {
  const parsed = parseDrawing(content);
  if (!parsed) return null;
  return (
    <svg className={`w-full h-full overflow-visible ${colorIconClass(parsed.color)}`} data-testid="shape-drawing">
      <path
        d={strokePath(parsed.points, true)}
        fill="none"
        stroke="currentColor"
        strokeWidth={parsed.width}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={parsed.highlighter ? CV.drawing.highlighterOpacity : 1}
      />
    </svg>
  );
}

const LINE_ARROW_MARKER_ID = "kf-line-shape-arrow";

// Renders 'line'/'free_edge' shapes as an SVG line between two independent
// endpoints (not a resizable box). Local coordinates are relative to the
// bounding box the store computed in toRFShape (min(x,x2), min(y,y2)).
function LineShapeBody({
  d,
  selected,
  onEndpointDragStart,
}: {
  d: CanvasShape;
  selected: boolean;
  onEndpointDragStart: (which: "a" | "b") => (e: React.MouseEvent) => void;
}) {
  const x2 = d.x2 ?? d.x;
  const y2 = d.y2 ?? d.y;
  const minX = Math.min(d.x, x2);
  const minY = Math.min(d.y, y2);
  const ax = d.x - minX;
  const ay = d.y - minY;
  const bx = x2 - minX;
  const by = y2 - minY;
  const kind = d.kind;
  const dashed = kind === "dashed_line";
  const isElbow = kind === "elbow";
  const arrowEnd = kind === "free_edge" || kind === "double_arrow" || kind === "elbow";
  const arrowStart = kind === "double_arrow";
  const needsMarker = arrowEnd || arrowStart;
  const testId =
    kind === "free_edge"    ? "shape-free-edge"   :
    kind === "dashed_line"  ? "shape-dashed-line" :
    kind === "double_arrow" ? "shape-double-arrow":
    kind === "elbow"        ? "shape-elbow"       :
    "shape-line";
  const size = CV.shape.endpointHandleSize;
  const markerRef = `url(#${LINE_ARROW_MARKER_ID})`;

  return (
    <>
      <svg
        className="w-full h-full overflow-visible"
        data-testid={testId}
      >
        {needsMarker && (
          <defs>
            {/* Open chevron (design mock), matching node-edge arrowheads.
                auto-start-reverse lets the same marker serve both ends. */}
            <marker
              id={LINE_ARROW_MARKER_ID}
              markerWidth="9"
              markerHeight="9"
              refX="7"
              refY="4.5"
              orient="auto-start-reverse"
            >
              <path
                d="M1,1 L8,4.5 L1,8"
                fill="none"
                stroke={THEME.raw.edgeArrowColor}
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </marker>
          </defs>
        )}
        {isElbow ? (
          <polyline
            points={`${ax},${ay} ${bx},${ay} ${bx},${by}`}
            fill="none"
            stroke={THEME.raw.edgeStroke}
            strokeWidth={THEME.raw.edgeStrokeWidth}
            markerEnd={arrowEnd ? markerRef : undefined}
          />
        ) : (
          <line
            x1={ax}
            y1={ay}
            x2={bx}
            y2={by}
            stroke={THEME.raw.edgeStroke}
            strokeWidth={THEME.raw.edgeStrokeWidth}
            strokeDasharray={dashed ? "6 5" : undefined}
            markerStart={arrowStart ? markerRef : undefined}
            markerEnd={arrowEnd ? markerRef : undefined}
          />
        )}
      </svg>
      {selected && (
        <>
          <div
            data-testid="line-endpoint-a"
            onMouseDown={onEndpointDragStart("a")}
            className="absolute rounded-full !bg-canvas-accent !border-canvas-accent border cursor-move nodrag"
            style={{ left: ax, top: ay, width: size, height: size, transform: "translate(-50%, -50%)" }}
          />
          <div
            data-testid="line-endpoint-b"
            onMouseDown={onEndpointDragStart("b")}
            className="absolute rounded-full !bg-canvas-accent !border-canvas-accent border cursor-move nodrag"
            style={{ left: bx, top: by, width: size, height: size, transform: "translate(-50%, -50%)" }}
          />
        </>
      )}
    </>
  );
}

function ShapeBody({ kind, content }: { kind: string; content?: string }) {
  const { fill, stroke } = THEME.shape;
  const boxStyle = { background: fill, border: `1.5px solid ${stroke}` };

  if (kind === "drawing") {
    return <DrawingBody content={content} />;
  }
  if (kind === "circle") {
    return (
      <div
        className={`w-full h-full rounded-full ${THEME.shape.circleBg}`}
        data-testid="shape-circle"
      />
    );
  }
  if (kind === "container_box") {
    return (
      <div
        className={`w-full h-full rounded-lg ${THEME.shape.containerBg}`}
        data-testid="shape-container"
      />
    );
  }
  if (kind === "diamond") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-diamond">
        <polygon points="50,2 98,50 50,98 2,50" fill={fill} stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "hexagon") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-hexagon">
        <polygon points="50,3 95,27 95,73 50,97 5,73 5,27" fill={fill} stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "triangle") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-triangle">
        <polygon points="50,3 97,97 3,97" fill={fill} stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "parallelogram") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-parallelogram">
        <polygon points="20,3 97,3 80,97 3,97" fill={fill} stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "cylinder") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-cylinder">
        <rect x="3" y="18" width="94" height="64" fill={fill} stroke="none" />
        <ellipse cx="50" cy="82" rx="47" ry="14" fill={fill} stroke={stroke} strokeWidth="1.5" />
        <ellipse cx="50" cy="18" rx="47" ry="14" fill="#e2e8f0" stroke={stroke} strokeWidth="1.5" />
        <line x1="3" y1="18" x2="3" y2="82" stroke={stroke} strokeWidth="1.5" />
        <line x1="97" y1="18" x2="97" y2="82" stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "arrow_right") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-arrow-right">
        <polygon points="3,30 70,30 70,10 97,50 70,90 70,70 3,70" fill={fill} stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "cross") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-cross">
        <polygon points="33,3 67,3 67,33 97,33 97,67 67,67 67,97 33,97 33,67 3,67 3,33 33,33" fill={fill} stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }

  // ── CSS box shapes (border-radius trick keeps the corner geometry crisp at
  //    any aspect ratio; SVG polygons distort under preserveAspectRatio=none) ──
  if (kind === "rounded_rect") {
    return <div className="w-full h-full rounded-lg" style={boxStyle} data-testid="shape-rounded-rect" />;
  }
  if (kind === "rectangle") {
    return <div className="w-full h-full" style={boxStyle} data-testid="shape-rectangle" />;
  }
  if (kind === "pill") {
    // rounded-full is a fixed 9999px radius → clamps to a true stadium (not an ellipse)
    return <div className="w-full h-full rounded-full" style={boxStyle} data-testid="shape-pill" />;
  }
  if (kind === "ellipse") {
    // 50% radius on each axis → a true ellipse that tracks the box aspect ratio
    return <div className="w-full h-full" style={{ ...boxStyle, borderRadius: "50%" }} data-testid="shape-ellipse" />;
  }
  if (kind === "highlight") {
    return (
      <div
        className="w-full h-full rounded-sm"
        style={{ background: "rgba(250, 204, 21, 0.35)" }}
        data-testid="shape-highlight"
      />
    );
  }

  // ── Flowchart primitives ────────────────────────────────────────────────────
  if (kind === "document") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-document">
        <path d="M3,5 H97 V78 Q84,92 68,78 Q52,64 36,78 Q20,92 3,78 Z" fill={fill} stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "predefined_process") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-predefined-process">
        <rect x="3" y="8" width="94" height="84" fill={fill} stroke={stroke} strokeWidth="1.5" />
        <line x1="15" y1="8" x2="15" y2="92" stroke={stroke} strokeWidth="1.5" />
        <line x1="85" y1="8" x2="85" y2="92" stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "manual_input") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-manual-input">
        <polygon points="3,28 97,8 97,92 3,92" fill={fill} stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "trapezoid") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-trapezoid">
        <polygon points="20,10 80,10 97,90 3,90" fill={fill} stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "offpage") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-offpage">
        <polygon points="3,5 97,5 97,62 50,95 3,62" fill={fill} stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "pentagon") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-pentagon">
        <polygon points="50,4 96,38 78,95 22,95 4,38" fill={fill} stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }

  // ── Geometry / annotation ───────────────────────────────────────────────────
  if (kind === "star") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-star">
        <polygon points="50,3 61,38 98,38 68,59 79,95 50,72 21,95 32,59 2,38 39,38" fill={fill} stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "octagon") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-octagon">
        <polygon points="30,3 70,3 97,30 97,70 70,97 30,97 3,70 3,30" fill={fill} stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "callout") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-callout">
        <path d="M6,6 H94 V66 H46 L28,94 L30,66 H6 Z" fill={fill} stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === "brace") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-brace">
        <path d="M72,4 Q40,4 40,26 Q40,50 14,50 Q40,50 40,74 Q40,96 72,96" fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === "bracket") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-bracket">
        <path d="M72,5 H30 V95 H72" fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === "sticky_note") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-sticky-note">
        <polygon points="4,4 78,4 96,22 96,96 4,96" fill={fill} stroke={stroke} strokeWidth="1.5" />
        <polygon points="78,4 78,22 96,22" fill={stroke} opacity="0.5" />
      </svg>
    );
  }

  // ── Software architecture ───────────────────────────────────────────────────
  if (kind === "cube") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-cube">
        <polygon points="4,30 71,30 71,96 4,96" fill={fill} stroke={stroke} strokeWidth="1.5" />
        <polygon points="4,30 21,8 88,8 71,30" fill="#e2e8f0" stroke={stroke} strokeWidth="1.5" />
        <polygon points="71,30 88,8 88,74 71,96" fill="#e2e8f0" stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "uml_component") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-uml-component">
        <rect x="16" y="8" width="81" height="84" fill={fill} stroke={stroke} strokeWidth="1.5" />
        <rect x="3" y="22" width="26" height="15" fill="#e2e8f0" stroke={stroke} strokeWidth="1.5" />
        <rect x="3" y="60" width="26" height="15" fill="#e2e8f0" stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "uml_package") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-uml-package">
        <polygon points="4,6 46,6 46,20 4,20" fill={fill} stroke={stroke} strokeWidth="1.5" />
        <rect x="4" y="20" width="92" height="74" fill={fill} stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "datastore") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-datastore">
        <line x1="4" y1="24" x2="97" y2="24" stroke={stroke} strokeWidth="1.5" />
        <line x1="4" y1="76" x2="97" y2="76" stroke={stroke} strokeWidth="1.5" />
        <line x1="4" y1="24" x2="4" y2="76" stroke={stroke} strokeWidth="1.5" />
        <line x1="22" y1="24" x2="22" y2="76" stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "actor") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-actor">
        <g fill="none" stroke={stroke} strokeWidth="4" strokeLinecap="round">
          <circle cx="50" cy="16" r="12" />
          <line x1="50" y1="28" x2="50" y2="64" />
          <line x1="24" y1="42" x2="76" y2="42" />
          <line x1="50" y1="64" x2="30" y2="94" />
          <line x1="50" y1="64" x2="70" y2="94" />
        </g>
      </svg>
    );
  }
  if (kind === "swimlane") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-swimlane">
        <rect x="2" y="2" width="96" height="96" fill={fill} fillOpacity="0.4" stroke={stroke} strokeWidth="1.5" />
        <line x1="2" y1="18" x2="98" y2="18" stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  if (kind === "queue_buffer") {
    return (
      <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="shape-queue-buffer">
        <path d="M90,10 H10 V90 H90" fill={fill} stroke={stroke} strokeWidth="1.5" />
        <path d="M90,10 Q74,50 90,90" fill="none" stroke={stroke} strokeWidth="1.5" />
      </svg>
    );
  }
  return (
    <div
      className={`w-full h-full rounded-lg ${THEME.shape.textboxBg} p-2 overflow-hidden`}
      data-testid="shape-textbox"
    >
      <p className={`text-xs ${THEME.shape.textboxText} leading-relaxed whitespace-pre-wrap`}>{content}</p>
    </div>
  );
}

const SPRING = { type: "spring", mass: 0.4, stiffness: 140, damping: 15 } as const;
const INSTANT = { duration: 0 } as const;

export interface ShapeNodeProps {
  shape: CanvasShape;
  selected?: boolean;
  /** Yellow ring shown while hovering this shape in reveal-edit mode. */
  revealHighlight?: boolean;
  /** Resolved per-node reveal visibility (container derives this from reveal mode + step). */
  revealVisible?: boolean;
  /** Gates the fade/scale-in transition to reveal mode only; outside it the shape is always shown, unanimated. */
  isRevealMode?: boolean;
  /** Whether the active tool permits entering content-edit mode (was `activeTool === "arrow"`). */
  editableOnDoubleClick?: boolean;
  /** Screen → flow-viewport coordinate conversion for endpoint drags (container supplies ReactFlow's). */
  screenToFlowPosition?: (p: { x: number; y: number }) => { x: number; y: number };
  onEndpointMove?: (which: "a" | "b", x: number, y: number) => void;
  onContentCommit?: (content: string) => void;
  onHoverChange?: (hovering: boolean) => void;
}

export default function ShapeNode({
  shape: d,
  selected = false,
  revealHighlight = false,
  revealVisible = true,
  isRevealMode = false,
  editableOnDoubleClick = false,
  screenToFlowPosition = (p) => p,
  onEndpointMove,
  onContentCommit,
  onHoverChange,
}: ShapeNodeProps) {
  const isLine = LINE_SHAPE_KINDS.has(d.kind);
  const isDrawing = d.kind === "drawing";

  const onEndpointDragStart = useCallback((which: "a" | "b") => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    function onMove(ev: MouseEvent) {
      const pos = screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
      onEndpointMove?.(which, pos.x, pos.y);
    }
    function onUp() {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [screenToFlowPosition, onEndpointMove]);

  const [isEditingContent, setIsEditingContent] = useState(false);
  const [editValue, setEditValue] = useState(d.content ?? "");
  const editorRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditingContent) {
      setEditValue(d.content ?? "");
      requestAnimationFrame(() => editorRef.current?.select());
    }
  }, [isEditingContent, d.content]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    if (!editableOnDoubleClick || isLine || isDrawing) return;
    e.stopPropagation();
    setIsEditingContent(true);
  }, [editableOnDoubleClick, isLine, isDrawing]);

  function commitContent() {
    setIsEditingContent(false);
    if (editValue !== (d.content ?? "")) onContentCommit?.(editValue);
  }

  return (
    <div
      className={`w-full h-full group ${revealHighlight ? "ring-2 ring-yellow-400 rounded-lg" : ""}`}
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
      onDoubleClick={handleDoubleClick}
    >
      {isLine ? (
        <LineShapeBody d={d} selected={!!selected} onEndpointDragStart={onEndpointDragStart} />
      ) : (
        <motion.div
          className="w-full h-full"
          initial={false}
          animate={revealVisible ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 12, scale: 0.92 }}
          transition={revealVisible && isRevealMode ? SPRING : INSTANT}
        >
          <ShapeBody kind={d.kind} content={d.content} />
        </motion.div>
      )}

      {!isLine && isEditingContent && (
        <textarea
          ref={editorRef}
          data-testid="shape-content-editor"
          className="absolute inset-2 z-10 bg-white/95 text-xs text-slate-800 rounded p-1.5 outline-none resize-none nodrag nopan"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitContent}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commitContent(); }
            if (e.key === "Escape") { e.stopPropagation(); setIsEditingContent(false); }
          }}
        />
      )}

      {!isLine && !isDrawing && !isEditingContent && d.kind !== "textbox" && d.content && (
        <div className="absolute inset-0 flex items-center justify-center p-2 pointer-events-none">
          <p className="text-xs text-slate-700 text-center break-words">{d.content}</p>
        </div>
      )}
    </div>
  );
}
